import { useEffect, useMemo, useRef, useState } from "react";

// Fallback: 50 major hubs used when the primary fetch fails
const FALLBACK_AIRPORTS = [
  { iata: "AMS", city: "Amsterdam",    country: "NL", name: "Amsterdam Airport Schiphol" },
  { iata: "LHR", city: "London",       country: "GB", name: "Heathrow Airport" },
  { iata: "CDG", city: "Paris",        country: "FR", name: "Charles de Gaulle Airport" },
  { iata: "FRA", city: "Frankfurt",    country: "DE", name: "Frankfurt Airport" },
  { iata: "MAD", city: "Madrid",       country: "ES", name: "Adolfo Suárez Madrid–Barajas Airport" },
  { iata: "BCN", city: "Barcelona",    country: "ES", name: "Barcelona–El Prat Airport" },
  { iata: "FCO", city: "Rome",         country: "IT", name: "Leonardo da Vinci International Airport" },
  { iata: "MXP", city: "Milan",        country: "IT", name: "Malpensa Airport" },
  { iata: "MUC", city: "Munich",       country: "DE", name: "Munich Airport" },
  { iata: "ZRH", city: "Zurich",       country: "CH", name: "Zurich Airport" },
  { iata: "VIE", city: "Vienna",       country: "AT", name: "Vienna International Airport" },
  { iata: "BRU", city: "Brussels",     country: "BE", name: "Brussels Airport" },
  { iata: "LIS", city: "Lisbon",       country: "PT", name: "Humberto Delgado Airport" },
  { iata: "CPH", city: "Copenhagen",   country: "DK", name: "Copenhagen Airport" },
  { iata: "ARN", city: "Stockholm",    country: "SE", name: "Stockholm Arlanda Airport" },
  { iata: "HEL", city: "Helsinki",     country: "FI", name: "Helsinki Airport" },
  { iata: "OSL", city: "Oslo",         country: "NO", name: "Oslo Gardermoen Airport" },
  { iata: "WAW", city: "Warsaw",       country: "PL", name: "Warsaw Chopin Airport" },
  { iata: "PRG", city: "Prague",       country: "CZ", name: "Václav Havel Airport Prague" },
  { iata: "BUD", city: "Budapest",     country: "HU", name: "Budapest Ferenc Liszt International Airport" },
  { iata: "ATH", city: "Athens",       country: "GR", name: "Athens International Airport" },
  { iata: "IST", city: "Istanbul",     country: "TR", name: "Istanbul Airport" },
  { iata: "DUB", city: "Dublin",       country: "IE", name: "Dublin Airport" },
  { iata: "DXB", city: "Dubai",        country: "AE", name: "Dubai International Airport" },
  { iata: "DOH", city: "Doha",         country: "QA", name: "Hamad International Airport" },
  { iata: "AUH", city: "Abu Dhabi",    country: "AE", name: "Abu Dhabi International Airport" },
  { iata: "SIN", city: "Singapore",    country: "SG", name: "Singapore Changi Airport" },
  { iata: "BKK", city: "Bangkok",      country: "TH", name: "Suvarnabhumi Airport" },
  { iata: "KUL", city: "Kuala Lumpur", country: "MY", name: "Kuala Lumpur International Airport" },
  { iata: "HKG", city: "Hong Kong",    country: "HK", name: "Hong Kong International Airport" },
  { iata: "ICN", city: "Seoul",        country: "KR", name: "Incheon International Airport" },
  { iata: "NRT", city: "Tokyo",        country: "JP", name: "Narita International Airport" },
  { iata: "PEK", city: "Beijing",      country: "CN", name: "Beijing Capital International Airport" },
  { iata: "PVG", city: "Shanghai",     country: "CN", name: "Shanghai Pudong International Airport" },
  { iata: "BOM", city: "Mumbai",       country: "IN", name: "Chhatrapati Shivaji Maharaj International Airport" },
  { iata: "DEL", city: "New Delhi",    country: "IN", name: "Indira Gandhi International Airport" },
  { iata: "KHI", city: "Karachi",      country: "PK", name: "Jinnah International Airport" },
  { iata: "LHE", city: "Lahore",       country: "PK", name: "Allama Iqbal International Airport" },
  { iata: "CGK", city: "Jakarta",      country: "ID", name: "Soekarno–Hatta International Airport" },
  { iata: "MNL", city: "Manila",       country: "PH", name: "Ninoy Aquino International Airport" },
  { iata: "JFK", city: "New York",     country: "US", name: "John F. Kennedy International Airport" },
  { iata: "LAX", city: "Los Angeles",  country: "US", name: "Los Angeles International Airport" },
  { iata: "MIA", city: "Miami",        country: "US", name: "Miami International Airport" },
  { iata: "ORD", city: "Chicago",      country: "US", name: "O'Hare International Airport" },
  { iata: "YYZ", city: "Toronto",      country: "CA", name: "Toronto Pearson International Airport" },
  { iata: "YHZ", city: "Halifax",      country: "CA", name: "Halifax Stanfield International Airport" },
  { iata: "GRU", city: "São Paulo",    country: "BR", name: "São Paulo–Guarulhos International Airport" },
  { iata: "JNB", city: "Johannesburg", country: "ZA", name: "O.R. Tambo International Airport" },
  { iata: "NBO", city: "Nairobi",      country: "KE", name: "Jomo Kenyatta International Airport" },
  { iata: "SYD", city: "Sydney",       country: "AU", name: "Sydney Airport" },
  { iata: "AKL", city: "Auckland",     country: "NZ", name: "Auckland Airport" },
];

// _promise is declared inside loadAirports block below

// Display name overrides for airports where the OurAirports name field is wrong,
// confusing, or contains a slash-separated municipality (e.g. "Muscat/Seeb International").
const NAME_OVERRIDES = {
  MCT: "Muscat International",
  ISB: "Islamabad International",
  BGY: "Milan Bergamo",
  MXP: "Milan Malpensa",
  CIA: "Rome Ciampino",
  BVA: "Paris Beauvais",
  HHN: "Frankfurt Hahn",
  CRL: "Brussels Charleroi",
  NYO: "Stockholm Skavsta",
};

// Manual overrides for airports whose OurAirports municipality differs from the
// commonly-known city name (e.g. secondary airports, relocated airports, etc.)
const CITY_OVERRIDES = {
  ISB: "Islamabad",
  IST: "Istanbul",
  NRT: "Tokyo",
  HND: "Tokyo",
  KIX: "Osaka",
  NGO: "Nagoya",
  CDG: "Paris",
  ORY: "Paris",
  BVA: "Paris",
  LGW: "London",
  STN: "London",
  LTN: "London",
  MSE: "London",
  MXP: "Milan",
  BGY: "Milan",
  TSF: "Venice",
  CIA: "Rome",
  NYO: "Stockholm",
  GOT: "Gothenburg",
  DWC: "Dubai",
  SXF: "Berlin",
  TXL: "Berlin",
  BER: "Berlin",
  HHN: "Frankfurt",
  EIN: "Eindhoven",
  CRL: "Brussels",
  BWE: "Hanover",
  VXO: "Vaxjo",
  KBP: "Kyiv",
  IEV: "Kyiv",
  MCT: "Muscat",
};

function parseOurAirportsCSV(text) {
  const lines = text.split("\n");
  const out = [];
  // Fixed column indices for OurAirports airports.csv:
  // 2=type, 3=name, 8=iso_country, 10=municipality, 13=iata_code
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;

    // Parse quoted CSV fields
    const fields = [];
    let cur = "";
    let inQ = false;
    for (let j = 0; j < line.length; j++) {
      const ch = line[j];
      if (ch === '"') {
        if (inQ && line[j + 1] === '"') { cur += '"'; j++; }
        else inQ = !inQ;
      } else if (ch === "," && !inQ) {
        fields.push(cur); cur = "";
      } else {
        cur += ch;
      }
    }
    fields.push(cur);

    const type = fields[2];
    const iata = fields[13];
    if (type !== "large_airport" && type !== "medium_airport") continue;
    if (!iata || iata.length !== 3) continue;

    // Strip regional suffixes like "(Roissy-en-France, Val-d'Oise)" from municipality
    const rawMunicipality = (fields[10] || "").split("(")[0].trim();
    const city = rawMunicipality || cleanName(fields[3]);
    const country = fields[8];
    const name = fields[3];
    if (!city || !country) continue;

    out.push({ iata, city, country, name });
  }
  return out;
}

// Remove the word "Airport" from names, and strip slash-separated
// municipality suffixes (e.g. "Muscat/Seeb" → "Muscat",
// "Frankfurt/Main" → "Frankfurt").
function cleanName(raw) {
  return (raw || "")
    .replace(/\/[^)]+/g, "")         // strip /Suburb and everything after (up to next paren)
    .replace(/\s+Airport\s+/gi, " ") // "Airport" in the middle
    .replace(/\s+Airport$/i, "")     // "Airport" at the end
    .trim();
}

// Bump this whenever airport data logic changes (NAME_OVERRIDES, CITY_OVERRIDES, parsing).
// A new key invalidates any sessionStorage entry from the previous version.
const CACHE_VERSION = "v4";
const CACHE_KEY = `airports_${CACHE_VERSION}`;

// Clear stale cache versions left by previous code
Object.keys(sessionStorage)
  .filter((k) => k.startsWith("airports_") && k !== CACHE_KEY)
  .forEach((k) => sessionStorage.removeItem(k));

// Module-level in-memory reference — avoids re-parsing within the same JS context
let _promise = null;

async function loadAirports() {
  // 1. In-memory: already processed this session
  const stored = sessionStorage.getItem(CACHE_KEY);
  if (stored) {
    try { return JSON.parse(stored); } catch { /* fall through to fetch */ }
  }

  // 2. Deduplicate concurrent callers
  if (_promise) return _promise;

  _promise = fetch(
    "https://raw.githubusercontent.com/davidmegginson/ourairports-data/main/airports.csv"
  )
    .then((r) => {
      if (!r.ok) throw new Error(`OurAirports fetch ${r.status}`);
      return r.text();
    })
    .then((csv) => {
      const raw = parseOurAirportsCSV(csv);
      const cityOverride = {
        ...Object.fromEntries(FALLBACK_AIRPORTS.map((a) => [a.iata, a.city])),
        ...CITY_OVERRIDES,
      };
      const airports = raw.map((a) => {
        const overrides = {};
        if (cityOverride[a.iata]) overrides.city = cityOverride[a.iata];
        if (NAME_OVERRIDES[a.iata]) overrides.name = NAME_OVERRIDES[a.iata];
        return Object.keys(overrides).length ? { ...a, ...overrides } : a;
      });

      const mct = airports.find((a) => a.iata === "MCT");
      if (mct) console.log("[AirportAutocomplete] MCT stored name:", mct.name);
      console.log(`[AirportAutocomplete] loaded ${airports.length} airports (cache ${CACHE_VERSION})`);

      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(airports)); } catch { /* storage full */ }
      return airports;
    })
    .finally(() => { _promise = null; });

  return _promise;
}

export default function AirportAutocomplete({ label, required, values, onChange, testId, placeholder }) {
  const { city, country, iata } = values;

  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  // Fetch once per session; fall back to FALLBACK_AIRPORTS on error
  useEffect(() => {
    loadAirports()
      .then((data) => { setAirports(data); setLoading(false); })
      .catch((err) => {
        console.warn("[AirportAutocomplete] fetch failed, using fallback:", err);
        setAirports(FALLBACK_AIRPORTS);
        setLoading(false);
      });
  }, []);

  const display = useMemo(() => {
    const cleanCity = (city || "").split("(")[0].trim();
    if (cleanCity && iata) return `${cleanCity} (${iata})`;
    if (cleanCity) return cleanCity;
    return "";
  }, [city, iata]);

  const [text, setText] = useState(display);

  useEffect(() => { setText(display); }, [display]);

  const matches = useMemo(() => {
    const q = (text || "").trim().toLowerCase();
    if (!airports.length || !q) return [];

    const out = [];
    for (const a of airports) {
      const iataL = a.iata.toLowerCase();
      const cityL = (a.city || "").toLowerCase();
      const nameL = (a.name || "").toLowerCase();
      const countryL = (a.country || "").toLowerCase();

      if (
        !iataL.includes(q) &&
        !cityL.includes(q) &&
        !nameL.includes(q) &&
        !countryL.includes(q)
      ) continue;

      let score;
      if (iataL === q)              score = 0;
      else if (iataL.startsWith(q)) score = 1;
      else if (cityL.startsWith(q)) score = 2;
      else if (cityL.includes(q))   score = 3;
      else if (nameL.includes(q))   score = 4;
      else                          score = 5;

      out.push({ ...a, score });
    }

    return out
      .sort((a, b) => a.score - b.score || a.city.localeCompare(b.city))
      .slice(0, 8);
  }, [airports, text]);

  // Close on outside click / touch
  useEffect(() => {
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, []);

  const select = (a) => {
    // Strip any parenthetical region suffix, e.g. "Paris (Roissy-en-France)" → "Paris"
    const rawCity = a.city || cleanName(a.name);
    const storedCity = rawCity.split("(")[0].trim();
    onChange({ city: storedCity, country: a.country, iata: a.iata });
    setText(`${storedCity} (${a.iata})`);
    setOpen(false);
  };

  const handleChange = (v) => {
    setText(v);
    onChange({ city: v, country, iata: "" });
    setOpen(true);
  };

  const q = (text || "").trim();
  const showDropdown = open && (loading || matches.length > 0 || (q && !loading));

  return (
    <div className="relative" ref={wrapRef}>
      <label className="ed-label">
        {label}{required && <span className="text-[#CC1A1A]"> *</span>}
      </label>

      <div className="relative">
        <input
          type="text"
          className="ed-input ed-input-lg"
          placeholder={placeholder || "City, airport name or IATA code…"}
          value={text}
          required={required}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { setOpen(true); setFocused(true); }}
          onBlur={() => setFocused(false)}
          autoComplete="off"
          data-testid={`airport-${testId}`}
        />
        {iata && !focused && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 rd-badge rd-badge-yellow !text-[0.6rem]">
            {iata}
          </span>
        )}
      </div>

      {showDropdown && (
        <ul
          className="absolute z-40 left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#CC1A1A] max-h-[280px] overflow-y-auto shadow-2xl"
          data-testid={`airport-${testId}-list`}
        >
          {loading && (
            <li className="px-4 py-3 text-[#888888] font-mono text-[0.65rem] tracking-widest uppercase">
              // loading airports…
            </li>
          )}
          {!loading && q && matches.length === 0 && (
            <li className="px-4 py-3 text-[#888888] font-mono text-[0.65rem] tracking-widest uppercase">
              // no airports found
            </li>
          )}
          {!loading && matches.map((a) => (
            <li
              key={a.iata}
              onMouseDown={(e) => { e.preventDefault(); select(a); }}
              className="px-4 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-[#2A2A2A] transition-colors border-b border-[#2A2A2A] last:border-b-0"
              data-testid={`airport-option-${a.iata}`}
            >
              <div className="min-w-0">
                <p className="font-mono text-sm text-white truncate">
                  {cleanName(a.name)} ({a.iata})
                </p>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#888888] mt-0.5 truncate">
                  {a.country} · {a.iata}
                </p>
              </div>
              <span className="rd-badge rd-badge-yellow !text-[0.6rem] shrink-0">{a.iata}</span>
            </li>
          ))}
        </ul>
      )}

      {iata && country && (
        <p className="mt-1 text-[11px] font-mono tracking-wider uppercase text-[#888888]">
          ✓ {country} · {iata}
        </p>
      )}
    </div>
  );
}
